import React, { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { Button, Input, Select, MoneyInput } from '../../components/v2/V2Components';
import BulkImportPanel from '../../components/v2/BulkImportPanel';
import UtilityService from '../../services/UtilityService';
import { DEFAULT_USAGE_UNITS } from '../../services/ConfigService';
import { toDateInputValue, formatCurrency, routeExtractedUnit } from '../../utils/billFormat';
import { formatMoney } from '../../utils/numericInput';
import { DAY_MS, toUtcDay } from '../../utils/billCoverage';
import { computeImportSignals } from '../../utils/billImportSignals';

/** Advisory lists are only useful if they're short enough to actually read. */
const MAX_ADVISORIES_SHOWN = 5;

const CUSTOM_UNIT = '__custom__';

const LABEL_CLASS =
  'block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-1';

/**
 * One blocked row, editable in place.
 *
 * Deliberately NOT a reuse of InlineBillForm: that component renders its own
 * Card, its own file input, its own autofill button and its own toasts, so
 * reusing it here would mean threading props to suppress half of it. This card
 * has one job — correct the one or two fields the parse missed — and the File
 * is still in memory, so saving attaches the right PDF.
 */
const NeedsAttentionCard = ({ item, utilityId, utilityType, onSaved, onSkip }) => {
  const fields = item.fields || {};
  const unitOptions = DEFAULT_USAGE_UNITS[utilityType] || [];

  const initialUnit = routeExtractedUnit(fields.usage_unit, unitOptions);
  const [periodStart, setPeriodStart] = useState(toDateInputValue(fields.period_start));
  const [periodEnd, setPeriodEnd] = useState(toDateInputValue(fields.period_end));
  const [statementDate, setStatementDate] = useState(toDateInputValue(fields.statement_date));
  // Formatted like MoneyInput displays it, so what is saved is what is shown.
  const [amount, setAmount] = useState(formatMoney(fields.amount));
  const [usageQuantity, setUsageQuantity] = useState(
    fields.usage_quantity != null ? String(fields.usage_quantity) : '',
  );
  const [usageUnit, setUsageUnit] = useState(initialUnit?.unit || unitOptions[0] || '');
  const [customUnit, setCustomUnit] = useState(Boolean(initialUnit?.custom));
  const [saving, setSaving] = useState(false);
  const [inlineError, setInlineError] = useState(null);

  const fieldId = (suffix) => `bill-fix-${item.clientId}-${suffix}`;

  const handleSave = async (e) => {
    e.preventDefault();
    if (!periodStart || !periodEnd || !amount) {
      setInlineError('Period and amount are required');
      return;
    }
    const numericAmount = parseFloat(amount);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      setInlineError('Amount must be greater than 0');
      return;
    }
    let numericUsage;
    if (usageQuantity !== '') {
      numericUsage = parseFloat(usageQuantity);
      if (!Number.isFinite(numericUsage) || numericUsage <= 0) {
        setInlineError('Usage must be greater than 0');
        return;
      }
    }

    try {
      setSaving(true);
      setInlineError(null);
      // Reuses the ordinary create endpoint — it already handles multipart,
      // validation, R2 upload and the duplicate 409.
      const created = await UtilityService.createBill(utilityId, {
        period_start: periodStart,
        period_end: periodEnd,
        statement_date: statementDate || undefined,
        amount: numericAmount,
        usage_quantity: numericUsage,
        usage_unit: numericUsage != null ? usageUnit || undefined : undefined,
        file: item.file,
      });
      onSaved(item.clientId, created);
    } catch (err) {
      setInlineError(err.message || 'Could not save this bill');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      aria-label={`Fix ${item.filename}`}
      onSubmit={handleSave}
      className="p-4 rounded-xl border border-[var(--border)] bg-[var(--bg-hover)] mb-3"
    >
      <div className="flex items-start gap-2 mb-3 min-w-0">
        <AlertCircle size={16} className="text-[var(--error)] shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[var(--text-main)] truncate">
            {item.filename}
          </p>
          <p className="text-xs text-[var(--text-secondary)]">{item.error}</p>
        </div>
      </div>

      {/* Real htmlFor/id pairs rather than the bare <label> the older bill
          form uses: several fix cards can be on screen at once, so each needs
          its own accessible name for a screen reader to tell them apart. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor={fieldId('start')} className={LABEL_CLASS}>
            Period Start
          </label>
          <Input
            id={fieldId('start')}
            type="date"
            value={periodStart}
            onChange={(e) => setPeriodStart(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor={fieldId('end')} className={LABEL_CLASS}>
            Period End
          </label>
          <Input
            id={fieldId('end')}
            type="date"
            value={periodEnd}
            onChange={(e) => setPeriodEnd(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor={fieldId('statement')} className={LABEL_CLASS}>
            Statement Date
          </label>
          <Input
            id={fieldId('statement')}
            type="date"
            value={statementDate}
            onChange={(e) => setStatementDate(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor={fieldId('amount')} className={LABEL_CLASS}>
            Amount (USD)
          </label>
          <MoneyInput
            id={fieldId('amount')}
            name="amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor={fieldId('usage')} className={LABEL_CLASS}>
            Usage
          </label>
          <Input
            id={fieldId('usage')}
            type="number"
            step="any"
            value={usageQuantity}
            onChange={(e) => setUsageQuantity(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor={fieldId('unit')} className={LABEL_CLASS}>
            Unit
          </label>
          {customUnit ? (
            <Input
              id={fieldId('unit')}
              maxLength={16}
              value={usageUnit}
              onChange={(e) => setUsageUnit(e.target.value)}
            />
          ) : (
            <Select
              id={fieldId('unit')}
              value={usageUnit}
              onChange={(e) => {
                if (e.target.value === CUSTOM_UNIT) {
                  setCustomUnit(true);
                  setUsageUnit('');
                } else {
                  setUsageUnit(e.target.value);
                }
              }}
            >
              {unitOptions.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
              <option value={CUSTOM_UNIT}>Add custom…</option>
            </Select>
          )}
        </div>
      </div>

      {inlineError && (
        <p className="text-xs text-[var(--error)] mt-3" role="alert">
          {inlineError}
        </p>
      )}

      <div className="flex flex-col sm:flex-row sm:justify-end gap-2 mt-4">
        <Button
          type="button"
          variant="ghost"
          className="w-full sm:w-auto"
          onClick={() => onSkip(item.clientId)}
        >
          Skip
        </Button>
        <Button type="submit" loading={saving} className="w-full sm:w-auto">
          Save bill
        </Button>
      </div>
    </form>
  );
};

/**
 * Advisory notes for a finished bill import: the historical-import caveat and
 * the "worth a look" signals. Computed over existing + created bills, deduped
 * by id — the page merges each created bill as it lands, so `existingBills`
 * already contains them and a naive concat would count each twice, shifting the
 * median baseline and inventing or hiding an advisory.
 */
const BillImportNotes = ({ createdBills, existingBills }) => {
  if (!createdBills.length) return null;

  // Compare as UTC day numbers: import responses carry RFC-1123 dates, which a
  // plain string sort orders by weekday name, not by date.
  const ends = createdBills.map((b) => toUtcDay(b.period_end)).filter((day) => day != null);
  const now = new Date();
  const cutoff = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) / DAY_MS - 365;
  // The trailing average only counts the last 12 months (api/utilities.py
  // _aggregate_for_utility), so a historical import moves it by zero. Say so,
  // or it reads as a bug.
  const coverageNote =
    ends.length && Math.min(...ends) < cutoff
      ? 'Some of these predate the last 12 months, so the trailing average above only reflects the most recent year.'
      : null;

  const byId = new Map();
  [...existingBills, ...createdBills].forEach((b) => {
    if (b && b.id != null) byId.set(b.id, b);
  });
  const signals = computeImportSignals([...byId.values()]);

  return (
    <>
      {coverageNote && <p className="text-xs text-[var(--text-secondary)] mb-4">{coverageNote}</p>}

      {signals.decimalShifts.length > 0 && (
        <div className="mb-4 p-3 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)]">
          <p className="text-xs font-semibold text-[var(--text-main)] mb-1">Worth a look</p>
          <ul className="text-xs text-[var(--text-secondary)] space-y-1">
            {signals.decimalShifts.slice(0, MAX_ADVISORIES_SHOWN).map((s) => (
              <li key={s.id}>
                {`${formatCurrency(s.amount)} looks about ${s.factor}× your usual ${formatCurrency(s.baseline)} — check the decimal point.`}
              </li>
            ))}
            {signals.decimalShifts.length > MAX_ADVISORIES_SHOWN && (
              <li>{`and ${signals.decimalShifts.length - MAX_ADVISORIES_SHOWN} more`}</li>
            )}
          </ul>
        </div>
      )}

      {signals.overlaps.length > 0 && (
        <p className="text-xs text-[var(--text-secondary)] mb-4">
          {`${signals.overlaps.length} pair${signals.overlaps.length === 1 ? '' : 's'} of bills cover overlapping periods — that will double-count in your average.`}
        </p>
      )}
    </>
  );
};

/**
 * Bulk bill import: drop N files, walk away, come back to N rows. The runner,
 * queue and summary are shared with the mortgage statement import
 * (BulkImportPanel); this supplies the bill-specific request, fix-it card and
 * notes.
 */
const BulkBillImport = ({ utilityId, utilityType, existingBills = [], onBillsCreated, onClose }) => (
  <BulkImportPanel
    title="Bulk Bills"
    noun={{ one: 'bill', many: 'bills' }}
    dropTitle="Drop your bill PDFs here"
    dropHint="One file per bill. We read each one and add it for you — you only get asked about files we can't read."
    inputTestId="bulk-file-picker-input"
    unavailableMessage="Bill scanning is unavailable right now — try again in a few minutes"
    importFile={(file, opts) => UtilityService.importBill(utilityId, file, opts)}
    recordOf={(result) => result.bill}
    onRecordsCreated={onBillsCreated}
    renderFixCard={({ item, onSaved, onSkip }) => (
      <NeedsAttentionCard
        item={item}
        utilityId={utilityId}
        utilityType={utilityType}
        onSaved={onSaved}
        onSkip={onSkip}
      />
    )}
    renderNotes={(createdBills) => (
      <BillImportNotes createdBills={createdBills} existingBills={existingBills} />
    )}
    onClose={onClose}
  />
);

export default BulkBillImport;
